/**
 * Phase 7 router — mobile-first shell with auth guards + bottom tab bar.
 *
 * Layout: every authed screen renders inside a centered `.app-shell` that
 * mimics the phone column in the wireframe. The shell holds the route's
 * `appbar` + page content, plus the persistent bottom tab bar (Play /
 * Wallet / Ranks / Profile). Unauthed (login, signup, etc.) and immersive
 * (live match) screens hide the tab bar.
 *
 * Route resolution:
 *   1. Exact-match routes -> `routes` table.
 *   2. Pattern routes (with path params) -> `PATTERN_ROUTES` list.
 *   3. Fallback: not-found.
 *
 * Auth: if a route requires auth and there's no token, we redirect to
 * `/onboarding` (which itself forwards into login/signup).
 */

import { auth } from './auth.js';
import { renderVerificationBanner } from './components/banner.js';
import { renderTabbar, shouldHideTabbar } from './components/tabbar.js';
import { renderAdminDashboard } from './pages/admin_dashboard.js';
import { renderAdminLive } from './pages/admin_live.js';
import { renderAdminV2Shell } from './pages/admin_v2/index.js';
import { renderAdminMetrics } from './pages/admin_metrics.js';
import { renderAdminPolls } from './pages/admin_polls.js';
import { renderAdminQuestions } from './pages/admin_questions.js';
import { renderAdminRequeryDeposit } from './pages/admin_requery_deposit.js';
import { renderAdminSuggestions } from './pages/admin_suggestions.js';
import { renderAdminUsers } from './pages/admin_users.js';
import { renderAdminWithdrawals } from './pages/admin_withdrawals.js';
import { renderBanks } from './pages/banks.js';
import { renderContest } from './pages/contest.js';
import { renderDeposit, renderDepositReturn } from './pages/deposit.js';
import { renderAntiCheatPolicy } from './pages/policy_anti_cheat.js';
import { renderErasers } from './pages/erasers.js';
import { renderFiftyFifty } from './pages/fiftyfifty.js';
import { renderForgotPassword } from './pages/forgot_password.js';
import { renderFriends } from './pages/friends.js';
import { renderHealth } from './pages/health.js';
import { renderHome } from './pages/home.js';
import { renderInterests } from './pages/interests.js';
import { renderInvite } from './pages/invite.js';
import { renderKyc } from './pages/kyc.js';
import { renderLive } from './pages/live.js';
import { renderLiveDetail } from './pages/live_detail.js';
import { renderLiveResults } from './pages/live_results.js';
import { renderLiveRoom } from './pages/live_room.js';
import { renderLogin } from './pages/login.js';
import { renderMatch } from './pages/match.js';
import { renderMatchResult } from './pages/match_result.js';
import { renderMe } from './pages/me.js';
import { renderNotFound } from './pages/not_found.js';
import { renderNotifications } from './pages/notifications.js';
import { renderNotificationPrefs } from './pages/notification_prefs.js';
import { renderOnboarding } from './pages/onboarding.js';
import { renderDuelsBrowse } from './pages/duels_browse.js';
import { renderDuelCreate } from './pages/duel_create.js';
import { renderDuelStatus } from './pages/duel_status.js';
import { renderDuelRun } from './pages/duel_run.js';
import { renderInviteJoin, renderPlay } from './pages/play.js';
import { renderSolo } from './pages/solo.js';
import { renderChatPrivacy } from './pages/chat_privacy.js';
import { renderConversation } from './pages/conversation.js';
import { renderMessageRequests, renderMessagesInbox } from './pages/messages_inbox.js';
import { renderPlayerProfile } from './pages/player_profile.js';
import { renderPollVote } from './pages/poll_vote.js';
import { renderProfile } from './pages/profile.js';
import { renderRanks } from './pages/ranks.js';
import { renderReferralRedirect } from './pages/referral_redirect.js';
import { renderResetPassword } from './pages/reset_password.js';
import { renderRetest } from './pages/retest.js';
import { renderShop } from './pages/shop.js';
import { renderSignup } from './pages/signup.js';
import { renderVerifyEmail } from './pages/verify_email.js';
import { renderWallet } from './pages/wallet.js';
import { renderWithdraw } from './pages/withdraw.js';

// Routes that DO NOT require an auth token.
const PUBLIC_ROUTES = new Set([
  '/onboarding',
  '/signup',
  '/login',
  '/verify-email',
  '/forgot-password',
  '/reset-password',
]);

// Public pattern matchers — same shape as PATTERN_ROUTES below but for
// auth-gating. Used by `needsAuth()` so an anonymous visitor hitting a
// pattern-based public route (like /r/CODE) is not bounced to onboarding
// before the page can stash the code.
const PUBLIC_PATTERN_ROUTES = [
  /^\/r\/[^/]+$/,
];

const routes = {
  '/': renderHome,
  '/onboarding': renderOnboarding,
  '/signup': renderSignup,
  '/login': renderLogin,
  '/verify-email': renderVerifyEmail,
  '/forgot-password': renderForgotPassword,
  '/reset-password': renderResetPassword,
  '/interests': renderInterests,
  '/profile': renderProfile,
  '/me': renderMe,
  '/kyc': renderKyc,
  '/ranks': renderRanks,
  '/wallet': renderWallet,
  '/wallet/deposit': renderDeposit,
  '/wallet/deposit/return': renderDepositReturn,
  '/wallet/withdraw': renderWithdraw,
  '/wallet/banks': renderBanks,
  '/erasers': renderErasers,
  '/policy/anti-cheat': renderAntiCheatPolicy,
  '/fiftyfifty': renderFiftyFifty,
  '/shop': renderShop,
  '/friends': renderFriends,
  '/invite': renderInvite,
  '/messages': renderMessagesInbox,
  '/messages/requests': renderMessageRequests,
  '/chat-privacy': renderChatPrivacy,
  '/notifications': renderNotifications,
  '/notifications/prefs': renderNotificationPrefs,
  '/live': renderLive,
  '/play': renderPlay,
  // Adjudication surface — where a user under investigation takes the
  // "defend your score" quiz. Rendered via the shared hardened
  // question view; NEVER writes account_state; power-ups don't apply.
  '/retest': renderRetest,
  '/play/solo': renderSolo,
  // Phase 8.12 rollout unified duel creation on the async flow. The
  // old /play/duel (classic Phase 5 live sync) now renders the same
  // async create page, so a single duel-creation experience covers
  // both "1-on-1" from Home and "Create" from the browser.
  '/play/duel': renderDuelCreate,
  '/duels': renderDuelsBrowse,
  '/duels/new': renderDuelCreate,
  '/admin': renderAdminDashboard,
  '/admin/v2': renderAdminV2Shell,
  '/admin/metrics': renderAdminMetrics,
  '/admin/questions': renderAdminQuestions,
  '/admin/live': renderAdminLive,
  '/admin/users': renderAdminUsers,
  '/admin/suggestions': renderAdminSuggestions,
  '/admin/withdrawals': renderAdminWithdrawals,
  '/admin/deposits/requery': renderAdminRequeryDeposit,
  '/admin/polls': renderAdminPolls,
  '/health': renderHealth,
};

// Dynamic patterns — pages parse their own ids from `window.location`.
const PATTERN_ROUTES = [
  // Admin v2 shell handles /admin/v2/<screen> and /admin/v2/players/:id
  // internally, so we register one pattern to catch every sub-path.
  { test: /^\/admin\/v2\/.+$/, render: renderAdminV2Shell },
  { test: /^\/r\/[^/]+$/, render: renderReferralRedirect },
  { test: /^\/u\/[^/]+$/, render: renderPlayerProfile },
  { test: /^\/polls\/[^/]+$/, render: renderPollVote },
  { test: /^\/play\/invite\/[^/]+$/, render: renderInviteJoin },
  { test: /^\/match\/[^/]+\/result$/, render: renderMatchResult },
  { test: /^\/match\/[^/]+$/, render: renderMatch },
  // Phase 8.12 — async duels. `/play` prefix reserved for the run
  // screen so the tabbar-hiding rule for the immersive run page can
  // pattern-match it without touching the status page.
  { test: /^\/duel\/[^/]+\/play$/, render: renderDuelRun },
  { test: /^\/duel\/[^/]+$/, render: renderDuelStatus },
  { test: /^\/contest\/[^/]+$/, render: renderContest },
  // /results + /play must be tested BEFORE /live/{id} so they don't get
  // swallowed by the more general pattern.
  { test: /^\/live\/[^/]+\/results$/, render: renderLiveResults },
  { test: /^\/live\/[^/]+\/play$/, render: renderLiveRoom },
  { test: /^\/live\/[^/]+$/, render: renderLiveDetail },
  // Phase 8.12 — messaging thread. `/messages/requests` is handled as
  // an exact route above, so this pattern only catches conversation ids.
  // `/messages/new/<user_id>` starts a compose-mode session that swaps
  // to `/messages/<conv_id>` after the first successful send.
  { test: /^\/messages\/new\/[^/]+$/, render: renderConversation },
  { test: /^\/messages\/[^/]+$/, render: renderConversation },
];

function resolve(path) {
  if (routes[path]) return routes[path];
  for (const r of PATTERN_ROUTES) {
    if (r.test.test(path)) return r.render;
  }
  return renderNotFound;
}

function needsAuth(path) {
  if (PUBLIC_ROUTES.has(path)) return false;
  for (const re of PUBLIC_PATTERN_ROUTES) {
    if (re.test(path)) return false;
  }
  return true;
}

function shouldShowBanner(path) {
  // Only show the "verify your email" banner on authed screens where it
  // doesn't disrupt the active flow.
  if (!auth.isAuthenticated()) return false;
  if (PUBLIC_ROUTES.has(path)) return false;
  // Don't pile the banner on top of a live match.
  if (/^\/match\/[^/]+$/.test(path)) return false;
  // Admin v2 owns its own chrome — no phone-column banner.
  if (path === '/admin/v2' || path.startsWith('/admin/v2/')) return false;
  return true;
}

function render(root) {
  const path = window.location.pathname;

  if (needsAuth(path) && !auth.isAuthenticated()) {
    history.replaceState(null, '', '/onboarding');
    return render(root);
  }

  const hideTabbar = shouldHideTabbar(path);
  // Admin v2 wants the whole viewport — no phone column, no paper background.
  const fullbleed = path === '/admin/v2' || path.startsWith('/admin/v2/');

  root.innerHTML = `
    <div class="app-shell ${hideTabbar ? 'no-tabbar' : ''} ${fullbleed ? 'fullbleed' : ''}">
      <div id="app-banner"></div>
      <main id="app-main">
        <div id="route-shell" class="shell"></div>
      </main>
      ${hideTabbar ? '' : '<div id="app-tabbar"></div>'}
    </div>
  `;

  if (shouldShowBanner(path)) {
    renderVerificationBanner(root.querySelector('#app-banner'));
  }

  const handler = resolve(path);
  handler(root.querySelector('#route-shell'));

  if (!hideTabbar) {
    renderTabbar(root.querySelector('#app-tabbar'), path);
  }
}

export function initRouter(root) {
  document.addEventListener('click', (e) => {
    const a = e.target.closest('a[data-link]');
    if (!a) return;
    // Skip if it's an external link, has a target, or modifier key is held.
    const href = a.getAttribute('href');
    if (!href || href.startsWith('http') || a.target === '_blank') return;
    if (e.metaKey || e.ctrlKey || e.shiftKey) return;
    e.preventDefault();
    history.pushState(null, '', href);
    render(root);
    // Scroll the route shell back to the top on navigation.
    const main = root.querySelector('#app-main');
    if (main) main.scrollTop = 0;
  });
  window.addEventListener('popstate', () => render(root));
  auth.onChange(() => render(root));
  render(root);
}

export function navigate(path) {
  history.pushState(null, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}
